/**
 * Board views: which stretch of the field's length the board shows.
 *
 * A view is a camera, not an edit — it changes what is on screen, never a
 * position, a phase or a drawing. Every view keeps the full width: only the
 * length is cropped, so no piece near a sideline ever leaves the board.
 */

import { FIELD_INSET } from './document.ts';
import type { PlayDocument } from './document.ts';
import { clamp, type Vec2 } from './geometry.ts';

export type BoardView = 'full' | 'left' | 'right';

export const BOARD_VIEWS: readonly BoardView[] = ['full', 'left', 'right'];

/**
 * How far a half view reaches past the halfway line, as a share of the length,
 * so the halfway line reads as a line rather than as the edge of the board.
 */
export const HALF_VIEW_OVERHANG = 0.04;

/** The visible stretch of the length, in field units. */
export interface ViewWindow {
  readonly from: number;
  readonly to: number;
}

export function viewWindow(view: BoardView): ViewWindow {
  switch (view) {
    case 'left':
      return { from: 0, to: 0.5 + HALF_VIEW_OVERHANG };
    case 'right':
      return { from: 0.5 - HALF_VIEW_OVERHANG, to: 1 };
    default:
      return { from: 0, to: 1 };
  }
}

export function nextView(view: BoardView): BoardView {
  return BOARD_VIEWS[(BOARD_VIEWS.indexOf(view) + 1) % BOARD_VIEWS.length]!;
}

export function isBoardView(value: unknown): value is BoardView {
  return typeof value === 'string' && (BOARD_VIEWS as readonly string[]).includes(value);
}

/** Clamps a point into what the view shows, keeping the usual inset from each edge. */
export function clampToView(p: Vec2, view: BoardView): Vec2 {
  const { from, to } = viewWindow(view);
  return {
    x: clamp(p.x, from + FIELD_INSET, to - FIELD_INSET),
    y: clamp(p.y, FIELD_INSET, 1 - FIELD_INSET),
  };
}

/** Whether a point lies within the view's stretch of the length. */
export function inView(p: Vec2, view: BoardView): boolean {
  const { from, to } = viewWindow(view);
  return p.x >= from && p.x <= to;
}

/**
 * The document as it should be stored with `view` recorded on it. Returns the
 * same object when nothing changes, so an autosaver that compares by identity
 * does not save a board just for having been opened. The full field is stored
 * as no view at all.
 */
export function withView(doc: PlayDocument, view: BoardView): PlayDocument {
  if ((doc.view ?? 'full') === view) return doc;
  if (view !== 'full') return { ...doc, view };
  const { view: _dropped, ...rest } = doc;
  return rest;
}
